import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { useRole } from "@/lib/roleContext";
import { ShieldCheck, ShieldAlert, Globe, EyeOff, Ticket, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const ARTIST_ROLE_LABEL = {
    owner: "Artist Owner",
    manager: "Artist Manager / Admin",
    team: "Team Member",
    staff: "Event Staff",
    admin: "Platform Admin",
};

const VERIFY_LABELS = {
    unverified: "Unverified — publish blocked",
    pending: "Pending admin review",
    needs_information: "More information requested",
    verified: "Verified",
    rejected: "Verification rejected",
};

const VIS_LABELS = {
    draft: "Draft — private to your workspace",
    public: "Public — live in Project Mint",
    hidden: "Hidden — not publicly listed",
    suspended: "Suspended — hidden by admin",
};

export default function Settings() {
    const { activeArtistId, activeArtistName, activeArtist, artists = [], setActiveArtist } = useRole();
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [verification, setVerification] = useState(null);

    useEffect(() => {
        if (!activeArtistId) return;
        endpoints.onboardingStatus(activeArtistId).then(setStatus).catch(() => setStatus(null));
        endpoints.getArtistVerification(activeArtistId).then(setVerification).catch(() => setVerification(null));
    }, [activeArtistId]);

    if (!activeArtistId) {
        return (
            <div className="pm-fade" data-testid="settings-page">
                <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Settings</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Workspace</h1>
                </header>
                <div className="p-6 md:p-10 text-sm text-[#a3a3a3]">
                    No active Artist workspace on this account.{" "}
                    <Link to="/onboarding/artist" className="text-[#ff4400] hover:underline">Set one up.</Link>
                </div>
            </div>
        );
    }

    const relRole = activeArtist?.role;
    const vStatus = (verification && verification.status) || (status && status.verification_status) || "unverified";
    const visibility = status?.visibility || "draft";
    const canPublish = !!status?.can_publish;

    return (
        <div className="pm-fade" data-testid="settings-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Settings</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Workspace</h1>
                <p className="mt-2 text-xs font-mono uppercase text-[#a3a3a3]">
                    Workspace-only settings live here. Universal account settings (email, password, name) live in{" "}
                    <Link to="/account" className="text-[#ff4400] hover:underline" data-testid="settings-account-link">Account settings</Link>.
                </p>
            </header>

            <div className="px-4 md:px-8 py-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <section className="border border-[#2a2a2a] p-6" data-testid="settings-active-artist">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Active Artist workspace</div>
                    <div className="mt-1 font-display font-black uppercase text-2xl truncate" data-testid="settings-active-name">
                        {activeArtistName}
                    </div>
                    <div className="mt-1 text-[10px] font-mono uppercase text-[#a3a3a3]">
                        Your role: <span className="text-[#ff4400]" data-testid="settings-active-role">{ARTIST_ROLE_LABEL[relRole] || relRole || "—"}</span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <Link to="/workspace/artist-page" className="pm-btn text-xs" data-testid="settings-goto-artist-page">
                            Edit Artist Page <ArrowRight className="w-3 h-3 ml-1 inline" />
                        </Link>
                        <Link to="/workspace/team" className="pm-btn text-xs" data-testid="settings-goto-team">
                            Manage Team <ArrowRight className="w-3 h-3 ml-1 inline" />
                        </Link>
                    </div>
                </section>

                <section className="border border-[#2a2a2a] p-6" data-testid="settings-verification">
                    <div className="text-[10px] font-mono uppercase text-[#666] flex items-center gap-2">
                        {vStatus === "verified" ? <ShieldCheck className="w-3 h-3 text-[#00e676]" /> : <ShieldAlert className="w-3 h-3 text-[#ffea00]" />}
                        Verification
                    </div>
                    <div className="mt-1 font-display font-bold uppercase text-lg" data-testid="settings-verification-status">
                        {VERIFY_LABELS[vStatus] || vStatus}
                    </div>
                    {vStatus === "needs_information" && verification?.admin_message && (
                        <div className="mt-2 border-l-2 border-[#ff9500] pl-3 text-xs text-[#f4f4f0]" data-testid="settings-verification-msg">
                            "{verification.admin_message}"
                        </div>
                    )}
                    {vStatus === "rejected" && verification?.decision_reason && (
                        <div className="mt-2 border-l-2 border-[#ff3333] pl-3 text-xs text-[#f4f4f0]" data-testid="settings-verification-reason">
                            "{verification.decision_reason}"
                        </div>
                    )}
                    {vStatus !== "verified" && (
                        <p className="mt-3 text-[10px] font-mono uppercase text-[#a3a3a3]">
                            Verification only controls public publishing. You can complete the profile checklist while unverified.
                        </p>
                    )}
                </section>

                <section className="border border-[#2a2a2a] p-6" data-testid="settings-visibility">
                    <div className="text-[10px] font-mono uppercase text-[#666] flex items-center gap-2">
                        {visibility === "public" ? <Globe className="w-3 h-3 text-[#00e676]" /> : <EyeOff className="w-3 h-3 text-[#a3a3a3]" />}
                        Publication status
                    </div>
                    <div className="mt-1 font-display font-bold uppercase text-lg" data-testid="settings-visibility-status">
                        {VIS_LABELS[visibility] || visibility}
                    </div>
                    <p className="mt-2 text-[10px] font-mono uppercase text-[#a3a3a3]">
                        {canPublish
                            ? "Minimum profile is complete."
                            : "Add profile image, city, country, primary genre, and a short bio to unlock publishing."}
                    </p>
                    <Link to="/workspace/artist-page" className="pm-btn pm-btn-primary text-xs mt-4 inline-flex" data-testid="settings-goto-publish">
                        Open Artist page <ArrowRight className="w-3 h-3 ml-1 inline" />
                    </Link>
                </section>

                <section className="border border-[#2a2a2a] p-6" data-testid="settings-checklist">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Profile completion</div>
                    <div className="mt-1 font-display font-bold uppercase text-lg">
                        {status ? `${status.done}/${status.total} · ${status.percent}%` : "—"}
                    </div>
                    <div className="mt-3 h-1 bg-[#141414]">
                        <div className="h-full bg-[#ff4400] transition-all" style={{ width: `${status?.percent || 0}%` }} />
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-1">
                        {status && Object.entries(status.checklist || {}).slice(0, 4).map(([k, done]) => (
                            <div key={k} className="text-[10px] font-mono uppercase text-[#a3a3a3] flex items-center gap-2">
                                <Check className={`w-3 h-3 ${done ? "text-[#00e676]" : "text-[#333]"}`} />
                                {k.replace(/_/g, " ")}
                            </div>
                        ))}
                    </div>
                </section>

                {artists.length > 1 && (
                    <section className="border border-[#2a2a2a] p-6 lg:col-span-2" data-testid="settings-switch-artist">
                        <div className="text-[10px] font-mono uppercase text-[#666]">Switch Artist workspace</div>
                        <div className="mt-2 text-[10px] font-mono uppercase text-[#a3a3a3]">
                            You have {artists.length} Artist workspaces on this account.
                        </div>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                            {artists.map((a) => (
                                <button
                                    key={a.id}
                                    onClick={() => {
                                        setActiveArtist(a.id, a.name);
                                        toast.success(`Switched workspace to ${a.name}`);
                                    }}
                                    data-testid={`settings-switch-${a.id}`}
                                    className={`text-left border p-3 hover:border-[#ff4400] transition-colors ${a.id === activeArtistId ? "border-[#ff4400] text-[#ff4400]" : "border-[#2a2a2a]"}`}
                                >
                                    <div className="font-display font-bold uppercase text-sm truncate">{a.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666]">{ARTIST_ROLE_LABEL[a.role] || a.role}</div>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                <section className="border border-[#2a2a2a] p-6 lg:col-span-2">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Exits</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Link to="/account" className="pm-btn text-xs" data-testid="settings-exit-account">
                            Account settings <ArrowRight className="w-3 h-3 ml-1 inline" />
                        </Link>
                        <Link to="/fan" className="pm-btn text-xs" data-testid="settings-exit-passport">
                            <Ticket className="w-3 h-3 mr-1 inline" /> Fan Passport
                        </Link>
                        <Link to="/" className="pm-btn text-xs" data-testid="settings-exit-discover">
                            Public Project Mint <ArrowRight className="w-3 h-3 ml-1 inline" />
                        </Link>
                    </div>
                </section>
            </div>
        </div>
    );
}
